import React, { ReactNode } from "react";
import { Link as GatsbyLink } from "gatsby";

import { Maybe, SanityBrandOrPage, SanitySlug, Scalars } from "@graphql-types";
import { setBaseUrl } from "@util/helper";
import { ButtonDesign, ButtonSize, IconKind, InternalLinkCustom } from "@util/types";
import { StyledLink } from "./link.style";
import { IconRightArrow } from "@util/assets";
import { A, Container } from "@util/standard";

interface Props {
  children?: ReactNode;
  linkText?: Maybe<Scalars["String"]>;
  externalLink?: Maybe<Scalars["String"]>;
  internalLink?: Maybe<SanityBrandOrPage | InternalLinkCustom>;
  className?: string;
  design?: ButtonDesign;
  useIcon?: IconKind;
  buttonSize?: ButtonSize;
  onClick?: () => void;
  slug?: Maybe<SanitySlug>;
}

interface LinkChildProps {
  children?: ReactNode;
  linkText?: Maybe<Scalars["String"]>;
  useIcon?: IconKind;
  design?: ButtonDesign;
  buttonSize?: ButtonSize;
}

const LinkChild = ({ children, linkText, useIcon, design, buttonSize }: LinkChildProps) => {
  if (children) {
    return (
      <StyledLink design={design} buttonSize={buttonSize} useIcon={useIcon}>
        <span>{children}</span>
        {useIcon && <span>{useIcon === "iconRightArrow" && <IconRightArrow />}</span>}
      </StyledLink>
    );
  }

  return (
    <StyledLink design={design} buttonSize={buttonSize} useIcon={useIcon}>
      <span>{linkText ?? "Button"}</span>
      {useIcon && <span>{useIcon === "iconRightArrow" && <IconRightArrow />}</span>}
    </StyledLink>
  );
};

const Link = ({
  linkText,
  internalLink,
  externalLink,
  className = "",
  children,
  design,
  useIcon,
  buttonSize,
  onClick,
  slug: productSlug,
}: Props) => {
  if (onClick) {
    return (
      <Container onClick={onClick} cursor="pointer">
        <LinkChild
          children={children}
          linkText={linkText}
          useIcon={useIcon}
          design={design}
          buttonSize={buttonSize}
        />
      </Container>
    );
  }

  if (internalLink) {
    const { slug, region, _type } = internalLink;

    if ((slug?.current == null || region?.iban == null || _type == null) && !productSlug)
      return console.error("link.tsx error"), null;
    let to;
    if (productSlug) {
      to = setBaseUrl(slug.current + productSlug, region.iban, _type) as string;
    } else {
      to = setBaseUrl(slug.current, region.iban, _type) as string;
    }

    return (
      <GatsbyLink activeClassName="active" className={className} to={to}>
        <LinkChild
          children={children}
          linkText={linkText}
          useIcon={useIcon}
          design={design}
          buttonSize={buttonSize}
        />
      </GatsbyLink>
    );
  }

  if (externalLink) {
    return (
      <A href={externalLink ?? "/"} className={className} target="_blank" rel="noreferrer">
        <LinkChild
          children={children}
          linkText={linkText}
          useIcon={useIcon}
          design={design}
          buttonSize={buttonSize}
        />
      </A>
    );
  }

  return null;
};

export default Link;
